import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

/**
 * Variant configurations for highlight boxes
 */
const VARIANT_CONFIG = {
  success: {
    borderColor: '#4caf50',
    backgroundColor: '#e8f5e9',
    iconColor: '#2e7d32',
    Icon: CheckCircleIcon
  },
  warning: {
    borderColor: '#ff9800',
    backgroundColor: '#fff3e0',
    iconColor: '#e65100',
    Icon: WarningIcon
  },
  info: {
    borderColor: '#2196f3',
    backgroundColor: '#e3f2fd',
    iconColor: '#1565c0',
    Icon: InfoIcon
  },
  error: {
    borderColor: '#f44336',
    backgroundColor: '#ffebee',
    iconColor: '#c62828',
    Icon: ErrorIcon
  }
};

/**
 * Styled Components
 */
const HighlightBox = superstyled({
  name: 'HighlightSection',
  slot: 'Box',
  tag: Box,
  styles: ({ theme, variant = 'info' }) => {
    const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
    return {
      padding: theme.spacing(2.5),
      borderLeft: `4px solid ${config.borderColor}`,
      backgroundColor: config.backgroundColor,
      borderRadius: theme.shape.rounded.md,
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'flex-start'
    };
  }
});

const IconWrapper = superstyled({
  name: 'HighlightSection',
  slot: 'IconWrapper',
  tag: Box,
  styles: ({ theme, variant = 'info' }) => {
    const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
    return {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: theme.spacing(3),
      height: theme.spacing(3),
      color: config.iconColor
    };
  }
});

const ContentWrapper = superstyled({
  name: 'HighlightSection',
  slot: 'ContentWrapper',
  tag: Box,
  styles: () => ({
    flex: 1
  })
});

/**
 * @typedef {object} HighlightContent
 * @property {string} variant - Visual variant: 'success', 'warning', 'info', or 'error'
 * @property {string} [title] - Optional title/heading for the highlight
 * @property {string} content - Main content text
 * @property {string} [customIcon] - Optional custom icon/emoji (overrides default)
 */

/**
 * HighlightSection Component
 *
 * Displays highlighted callout boxes for key insights, warnings, tips, etc.
 * Supports different visual variants for different types of information.
 *
 * @param {object} props - Component props
 * @param {HighlightContent} props.content - Highlight content
 * @returns {import('react').ReactElement} The highlight section component
 *
 * @example
 * <HighlightSection content={{
 *   variant: 'success',
 *   title: '🎯 Key Opportunity',
 *   content: 'Market growing at 15% annually with low competition...'
 * }} />
 *
 * @example
 * <HighlightSection content={{
 *   variant: 'warning',
 *   title: 'Challenge',
 *   content: 'High customer acquisition costs in this market.'
 * }} />
 */
function HighlightSection({ content }) {
  if (!content || !content.content) {
    return null;
  }

  const { variant = 'info', title, content: text, customIcon } = content;
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;
  const IconComponent = config.Icon;

  return (
    <HighlightBox variant={variant}>
      <IconWrapper variant={variant}>
        {customIcon ? <Text size="lg">{customIcon}</Text> : <IconComponent fontSize="small" />}
      </IconWrapper>
      <ContentWrapper>
        {title && (
          <Text mb={1} size="md" weight="medium">
            {title}
          </Text>
        )}
        <Text color="secondary" size="sm">
          {text}
        </Text>
      </ContentWrapper>
    </HighlightBox>
  );
}

HighlightSection.propTypes = {
  content: PropTypes.shape({
    variant: PropTypes.oneOf(['success', 'warning', 'info', 'error']).isRequired,
    title: PropTypes.string,
    content: PropTypes.string.isRequired,
    customIcon: PropTypes.string
  }).isRequired
};

export default HighlightSection;
